import { create } from 'zustand';
import { get as apiGet, patch as apiPatch } from '../api/client';

/**
 * 会员状态（服务端下发的单一事实源）。
 * 对应后端 GET /membership/status，字段经 ResponseInterceptor + client 转 camelCase。
 * features 为后端功能权限 key（snake_case，如 export_csv），由 usePermission 映射到前端 key。
 */
export interface MembershipStatus {
  levelCode: string;
  levelName: string;
  expireAt: string | null;
  autoRenew: boolean;
  isActive: boolean;
  daysRemaining: number | null;
  features: string[];
}

interface MembershipState {
  status: MembershipStatus | null;
  loading: boolean;
  /** 已加载状态对应的用户 id，用于跨用户切换时判定是否需要重新拉取 */
  loadedFor: string | null;
  /** 按用户 id 加载会员状态；同一用户已加载则跳过（force 可强制刷新） */
  load: (userId: string, force?: boolean) => Promise<void>;
  /** 强制刷新当前已加载用户的会员状态（支付/调整成功后调用） */
  refresh: () => Promise<void>;
  /** 开关自动续费，成功后刷新状态 */
  setAutoRenew: (autoRenew: boolean) => Promise<{ ok: boolean; message?: string }>;
  /** 清空（登出或用户切换时） */
  reset: () => void;
}

/**
 * 会员状态 store：为 usePermission 提供「同步可读」的服务端权限数据。
 * 采用 loadedFor 按用户 id 去重，避免多组件并发重复请求，也避免跨用户脏读。
 */
export const useMembershipStore = create<MembershipState>()((set, get) => ({
  status: null,
  loading: false,
  loadedFor: null,

  load: async (userId, force) => {
    if (!userId) return;
    const { loading, loadedFor } = get();
    if (loading) return;
    if (loadedFor === userId && !force) return;
    set({ loading: true });
    try {
      const status = await apiGet<MembershipStatus>('/membership/status');
      set({ status, loadedFor: userId, loading: false });
    } catch {
      // 拉取失败不阻塞界面：usePermission 会回落到 profile 的基础判定
      set({ loading: false });
    }
  },

  refresh: async () => {
    const uid = get().loadedFor;
    if (uid) await get().load(uid, true);
  },

  setAutoRenew: async (autoRenew) => {
    try {
      await apiPatch('/membership/auto-renew', { autoRenew });
      await get().refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : '操作失败' };
    }
  },

  reset: () => set({ status: null, loadedFor: null, loading: false }),
}));
